/* AtmosphereStrip — current condition chips (temp, humidity, wind).
 * Extracted from TodayPage so VerdictPage can mount the same row.
 */
import { motion } from "framer-motion";
import {
  Drop,
  NavigationArrow,
  Thermometer,
  Wind,
} from "@phosphor-icons/react";
import { useSettings } from "@/lib/settings";
import { formatTemp, formatWind, windUnitLabel } from "@/lib/units";

export default function AtmosphereStrip({ forecast, theme }) {
  const { settings } = useSettings();
  const cur = forecast?.current || {};
  if (cur.temperature_2m == null) return null;

  return (
    <div
      className="flex items-center gap-1.5 flex-nowrap overflow-x-auto pb-0.5"
      data-testid="today-conditions"
      style={{ scrollbarWidth: "none" }}
    >
      <Chip
        theme={theme}
        icon={<Thermometer size={16} weight="duotone" />}
        label={formatTemp(cur.temperature_2m, settings.tempUnit)}
        sub={
          cur.apparent_temperature != null &&
          Math.abs(cur.apparent_temperature - cur.temperature_2m) >= 1.5
            ? `feels ${formatTemp(cur.apparent_temperature, settings.tempUnit)}`
            : null
        }
      />
      {cur.relative_humidity_2m != null && (
        <Chip
          theme={theme}
          icon={<Drop size={16} weight="duotone" />}
          label={`${Math.round(cur.relative_humidity_2m)}%`}
          sub="humid"
        />
      )}
      {cur.wind_speed_10m != null && (
        <Chip
          theme={theme}
          icon={
            cur.wind_direction_10m != null ? (
              <motion.span
                className="inline-flex"
                initial={{ rotate: cur.wind_direction_10m + 180 }}
                animate={{ rotate: cur.wind_direction_10m + 180 }}
                transition={{ type: "spring", stiffness: 110, damping: 18 }}
                style={{ transformOrigin: "50% 50%" }}
              >
                <NavigationArrow size={14} weight="fill" />
              </motion.span>
            ) : (
              <Wind size={16} weight="duotone" />
            )
          }
          label={formatWind(cur.wind_speed_10m, settings.windUnit)}
          sub={
            cur.wind_direction_10m != null
              ? `${windUnitLabel(settings.windUnit)} from ${compassFromDeg(cur.wind_direction_10m)}`
              : windUnitLabel(settings.windUnit)
          }
        />
      )}
    </div>
  );
}

function compassFromDeg(deg) {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}

function Chip({ theme, icon, label, sub }) {
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shrink-0"
      style={{
        background: theme.surface,
        border: `1px solid ${theme.surfaceBorder}`,
        color: theme.accent,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      {icon}
      <span className="font-semibold tabular-nums" style={{ fontSize: 13, color: theme.textPrimary }}>
        {label}
      </span>
      {sub && (
        <span className="text-[10px]" style={{ color: theme.textMuted }}>
          {sub}
        </span>
      )}
    </div>
  );
}
